import { Suspense, useEffect } from 'react';
import { SkeletonPlaceHolder } from '@pu/mpp-ui/components/skeleton';
import { useCSS } from '@pu/mpp-ui/hooks';
import { useInsider } from 'core/contexts/insider.context';
import usePaginatedCatalogs from 'core/hooks/products/use-paginated-catalogs';
import { ESuggestedProductViewId } from 'core/model/enums/catalog.enum';
import { EProductOrigin } from 'core/model/enums/gtm.enum';
import { hasBannersSelector } from 'core/store/customization/selectors';
import { navigationSelector } from 'core/store/navigation/selectors';
import { useAppSelector } from 'core/store/store.utils';
import { CarouselCardPromotions } from 'screens/home/components/carousel-card-promotions';
import { CarouselCardSuggestion } from 'screens/suggested-products/components';
import { useSuggestions } from 'screens/suggested-products/hooks/use-suggestions';
import CarouselSkeleton from 'shared/components/products/carousel-skeleton';
import { CatalogCarouselCardProducts } from 'shared/components/products/catalog-carousel-card-products';
import { SliderLoading } from 'shared/components/slider/components/slider-loader';
import { pushInsiderHomePage } from 'shared/utils/insider.utils';
import { ShortcutList } from './components/shortcut-list';
import { TopbarBanner } from './components/topbar-banner';
import { LazySlidesBanner } from './home.lazy';

import './home.scss';

const Home = () => {
  const css = useCSS('home');
  const { categories } = useAppSelector(navigationSelector);
  const hasBanners = useAppSelector(hasBannersSelector);
  const { insiderIsReady } = useInsider();
  const { suggestions, areSuggestionsLoading } = useSuggestions({
    sectionId: ESuggestedProductViewId.homepage,
    autoLoad: false,
  });
  const { catalogs, isLoading, lastItemRef, isIdle } = usePaginatedCatalogs(
    categories.list.map(item => ({ id: item.id.toString(), name: item.name })),
  );

  useEffect(() => {
    if (insiderIsReady) pushInsiderHomePage();
  }, [insiderIsReady]);

  return (
    <div className={css()}>
      <TopbarBanner />
      <div className={css('container')}>
        {hasBanners && (
          <Suspense fallback={<SliderLoading />}>
            <LazySlidesBanner />
          </Suspense>
        )}
        <ShortcutList />
        <div className={css('products')}>
          <CarouselCardPromotions isIdle={isIdle} />
          <SkeletonPlaceHolder
            loading={areSuggestionsLoading}
            skeleton={<CarouselSkeleton ariaLabel="cargando productos sugeridos" />}
          >
            {suggestions?.map((catalog, index) => (
              <CarouselCardSuggestion
                key={`${catalog.title}-${index}`}
                catalog={catalog}
                origin={EProductOrigin.SUGGESTED_PRODUCTS_HOME}
                suggestedProductType={catalog.type}
              />
            ))}
          </SkeletonPlaceHolder>
          <CatalogCarouselCardProducts catalogs={catalogs} origin={EProductOrigin.HOME} />
          <div ref={lastItemRef} id="last-item">
            {(isLoading || isIdle) && <CarouselSkeleton />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
